import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { device } from "../../../atoms/Utlis/media";
import {
  FileText,
  CreditCard,
  RefreshCw,
  Loader2,
  XCircle,
  Wallet,
  TrendingUp,
  TrendingDown,
  MessageCircle,
  Sparkles,
  Receipt,
  ArrowDownLeft,
  ArrowUpRight,
} from "lucide-react";
import EmptyState from "../../../atoms/Dashboard/WhatsApp/EmptyState";
import { Link } from "react-router-dom";
import BackButtonIconPng from "../../../../assets/Icons/SingleAlbum/back.png";
import { useUsage } from "../../../../hooks/useUsage";
import { useWalletRecharge } from "../../../../hooks/useWalletRecharge";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { clearRechargeOrder } from "../../../../Redux/Slice/Dashboard/WhatsAppSlice";
import { getWalletTransactions } from "../../../../Redux/ApiCalls/Dashboard/WhatsAppAPI";
import { SkeletonBilling } from "../../../atoms/Dashboard/WhatsApp/SkeletonLoader";

import {
  PageContainer,
  ErrorText,
  ModalOverlay,
  ModalContent,
  CloseButton,
  ModalTitle,
  ModalActions,
  PrimaryButton,
  SecondaryButton,
} from "./whatsappSharedStyles";

const formatCurrency = (amount?: string, currency?: string) => {
  if (amount == null) return "—";
  const symbol = currency === "INR" || !currency ? "₹" : currency;
  return `${symbol}${parseFloat(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatCurrencyShort = (amount?: string, currency?: string) => {
  if (amount == null) return "—";
  const symbol = currency === "INR" || !currency ? "₹" : currency;
  return `${symbol}${parseFloat(amount).toLocaleString()}`;
};

const BillingUsagePage: React.FC = () => {
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);
  const isBusinessUser = user?.role === 3;

  const {
    walletBalance,
    walletLoading,
    walletTransactions,
    walletTransactionsPage,
    walletTransactionsTotalPages,
    loading,
    error,
    isError,
    rechargeOrders,
    rechargeOrdersPage,
    rechargeOrdersTotalPages,
    rechargeOrdersLoading,
    refresh,
    loadMoreRechargeOrders,
  } = useUsage();

  const {
    rechargeOrder,
    rechargeLoading,
    rechargeVerifying,
    initiateRecharge,
    openCashfreeCheckout,
    cancelOrder,
    verifyPayment,
  } = useWalletRecharge();

  const walletData = walletBalance?.data;

  const [txType, setTxType] = useState<string>("credit");
  const [txCategory, setTxCategory] = useState<string>("recharge");

  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [showFeePreview, setShowFeePreview] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("order_id");
    if (orderId) {
      verifyPayment(orderId)
        .then(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
          refresh();
        })
        .catch(() => { });
    }
  }, [verifyPayment, refresh]);

  useEffect(() => {
    dispatch(
      getWalletTransactions({
        page: 1,
        page_size: 20,
        type: txType,
        category: txCategory,
      })
    );
  }, [dispatch, txType, txCategory]);

  const handleRefresh = () => {
    refresh();
    dispatch(
      getWalletTransactions({
        page: 1,
        page_size: 20,
        type: txType,
        category: txCategory,
      })
    );
  };

  const handleLoadMoreTransactions = () => {
    if (loading || walletTransactionsPage >= walletTransactionsTotalPages) return;
    dispatch(
      getWalletTransactions({
        page: walletTransactionsPage + 1,
        page_size: 20,
        type: txType,
        category: txCategory,
      })
    );
  };

  const handleProceedRecharge = async () => {
    if (!rechargeAmount || parseFloat(rechargeAmount) <= 0) return;
    try {
      await initiateRecharge(rechargeAmount);
      setShowFeePreview(true);
    } catch {
      // handled by redux / toast elsewhere
    }
  };

  const handlePay = async () => {
    if (rechargeOrder?.payment_session_id) {
      await openCashfreeCheckout(
        rechargeOrder.payment_session_id,
        rechargeOrder.order_id
      );
    }
  };

  const handleCancelRecharge = async () => {
    if (rechargeOrder?.order_id) {
      try {
        await cancelOrder(rechargeOrder.order_id);
      } catch {
        // ignore cancel errors
      }
    }
    setShowFeePreview(false);
    setRechargeAmount("");
    dispatch(clearRechargeOrder());
  };

  const handleCloseModal = () => {
    setIsRechargeModalOpen(false);
    setShowFeePreview(false);
    setRechargeAmount("");
    dispatch(clearRechargeOrder());
  };

  if (walletLoading && !walletData) {
    return (
      <PageContainer>
        <SkeletonBilling />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackLink to="/dashboard/whatsapp" aria-label="Go back">
              <img
                src={BackButtonIconPng}
                style={{ width: "19.6px", height: "16.8px" }}
                alt="Back"
              />
            </BackLink>
            <HeroText>
              <HeroBadge>
                <Sparkles size={13} />
                Wallet & usage
              </HeroBadge>
              <PageTitle className="linear-text">Billing & Usage</PageTitle>
              <PageSubtitle>
                Wallet balance, message usage, and transaction history
              </PageSubtitle>
            </HeroText>
          </HeroLeft>
          <HeroActions>
            {isBusinessUser && (
              <RechargeButton onClick={() => setIsRechargeModalOpen(true)}>
                <CreditCard size={16} />
                Recharge wallet
              </RechargeButton>
            )}
            <RefreshButton onClick={handleRefresh} title="Refresh">
              <RefreshCw size={18} />
            </RefreshButton>
          </HeroActions>
        </HeroContent>
      </Hero>

      {isError && error?.wallet && <ErrorText>{error.wallet}</ErrorText>}
      {isError && error?.transactions && (
        <ErrorText>{error.transactions}</ErrorText>
      )}

      {rechargeVerifying && (
        <VerificationAlert>
          <Loader2 size={16} className="spin" />
          Verifying payment with Cashfree gateway... Please wait.
        </VerificationAlert>
      )}

      {walletData && (
        <>
          <StatsRow>
            <WalletHero>
              <WalletHeroTop>
                <WalletIconWrap>
                  <Wallet size={22} strokeWidth={2} />
                </WalletIconWrap>
                <WalletHeroLabel>Current balance</WalletHeroLabel>
              </WalletHeroTop>
              <WalletHeroValue>
                {formatCurrency(walletData.balance, walletData.currency)}
              </WalletHeroValue>
              <WalletHeroFooter>
                <StatusPill $active={walletData.is_active}>
                  {walletData.is_active ? "Active wallet" : "Inactive wallet"}
                </StatusPill>
                {isBusinessUser && (
                  <QuickRechargeLink
                    type="button"
                    onClick={() => setIsRechargeModalOpen(true)}
                  >
                    Top up now →
                  </QuickRechargeLink>
                )}
              </WalletHeroFooter>
            </WalletHero>

            <StatCard>
              <StatIconWrap $tone="credit">
                <TrendingUp size={20} strokeWidth={2} />
              </StatIconWrap>
              <StatContent>
                <StatLabel>Total credited</StatLabel>
                <StatValue>
                  {formatCurrencyShort(
                    walletData.total_credited,
                    walletData.currency
                  )}
                </StatValue>
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconWrap $tone="debit">
                <TrendingDown size={20} strokeWidth={2} />
              </StatIconWrap>
              <StatContent>
                <StatLabel>Total debited</StatLabel>
                <StatValue>
                  {formatCurrencyShort(
                    walletData.total_debited,
                    walletData.currency
                  )}
                </StatValue>
              </StatContent>
            </StatCard>
          </StatsRow>

          <UsageHighlight>
            <UsageHighlightIcon>
              <MessageCircle size={22} strokeWidth={2} />
            </UsageHighlightIcon>
            <UsageHighlightContent>
              <UsageHighlightLabel>Template conversations billed</UsageHighlightLabel>
              <UsageHighlightValue>
                {walletData.template_conversations_billed?.toLocaleString() ?? 0}
              </UsageHighlightValue>
            </UsageHighlightContent>
            <UsageHighlightNote>Messages charged to your wallet</UsageHighlightNote>
          </UsageHighlight>

          {walletData.billing_note && (
            <BillingNoteCard>
              <BillingNoteTitle>Billing note</BillingNoteTitle>
              <BillingNoteText>{walletData.billing_note}</BillingNoteText>
            </BillingNoteCard>
          )}
        </>
      )}

      <TransactionSection>
        <SectionHeader>
          <SectionTitle>
            <Receipt size={20} />
            Transaction history
          </SectionTitle>
          <SectionHint>Filter by credits or usage charges</SectionHint>
        </SectionHeader>

        <TabHeader>
          <TabButton
            $active={txType === "credit"}
            onClick={() => {
              setTxType("credit");
              setTxCategory("recharge");
            }}
          >
            <ArrowDownLeft size={14} />
            Recharges
          </TabButton>
          <TabButton
            $active={txType === "debit"}
            onClick={() => {
              setTxType("debit");
              setTxCategory("");
            }}
          >
            <ArrowUpRight size={14} />
            Usage charges
          </TabButton>
        </TabHeader>

        {walletTransactions.length === 0 ? (
          <EmptyInvoices>
            <EmptyState
              icon={<FileText size={40} />}
              title="No transactions yet"
              description="Transactions will appear here after your first recharge or message charge."
            />
          </EmptyInvoices>
        ) : (
          <>
            <TransactionList>
              {walletTransactions.map((tx) => (
                <TransactionRow key={tx.id} $type={tx.type}>
                  <TransactionIcon $type={tx.type}>
                    {tx.type === "credit" ? (
                      <ArrowDownLeft size={16} />
                    ) : (
                      <ArrowUpRight size={16} />
                    )}
                  </TransactionIcon>
                  <TransactionInfo>
                    <TransactionDesc>{tx.description}</TransactionDesc>
                    <TransactionDate>
                      {new Date(tx.created_at).toLocaleDateString([], {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </TransactionDate>
                  </TransactionInfo>
                  <TransactionRight>
                    <TransactionAmount $type={tx.type}>
                      {tx.type === "credit" ? "+" : "−"}{" "}
                      {formatCurrencyShort(tx.amount)}
                    </TransactionAmount>
                    <TransactionBadge $type={tx.type}>
                      {tx.category}
                    </TransactionBadge>
                  </TransactionRight>
                </TransactionRow>
              ))}
            </TransactionList>
            {walletTransactionsPage < walletTransactionsTotalPages && (
              <LoadMoreButton
                onClick={handleLoadMoreTransactions}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    Loading…
                  </>
                ) : (
                  "Load more transactions"
                )}
              </LoadMoreButton>
            )}
          </>
        )}
      </TransactionSection>

      {isBusinessUser && (
        <TransactionSection>
          <SectionHeader>
            <SectionTitle>
              <CreditCard size={20} />
              Recharge order history
            </SectionTitle>
            <SectionHint>Track your wallet top-up orders</SectionHint>
          </SectionHeader>

          {rechargeOrders.length === 0 ? (
            <EmptyInvoices>
              <EmptyState
                icon={<CreditCard size={40} />}
                title="No recharge orders yet"
                description="Your recharge history will appear here."
              />
            </EmptyInvoices>
          ) : (
            <>
              <TransactionList>
                {rechargeOrders.map((order) => {
                  const isSuccess =
                    order.status === "paid" || order.status === "credited";
                  return (
                    <TransactionRow
                      key={order.order_id}
                      $type={isSuccess ? "credit" : "debit"}
                    >
                      <TransactionIcon $type={isSuccess ? "credit" : "debit"}>
                        <CreditCard size={16} />
                      </TransactionIcon>
                      <TransactionInfo>
                        <TransactionDesc>Order #{order.order_id}</TransactionDesc>
                        <TransactionDate>
                          {new Date(order.created_at).toLocaleDateString([], {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TransactionDate>
                      </TransactionInfo>
                      <TransactionRight>
                        <TransactionAmount $type={isSuccess ? "credit" : "debit"}>
                          {formatCurrencyShort(order.amount_requested)}
                        </TransactionAmount>
                        <StatusBadge $status={order.status}>
                          {order.status}
                        </StatusBadge>
                      </TransactionRight>
                    </TransactionRow>
                  );
                })}
              </TransactionList>
              {rechargeOrdersPage < rechargeOrdersTotalPages && (
                <LoadMoreButton
                  onClick={loadMoreRechargeOrders}
                  disabled={rechargeOrdersLoading}
                >
                  {rechargeOrdersLoading ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      Loading…
                    </>
                  ) : (
                    "Load more orders"
                  )}
                </LoadMoreButton>
              )}
            </>
          )}
        </TransactionSection>
      )}

      {isRechargeModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <StyledModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>
              <XCircle size={24} />
            </CloseButton>
            <ModalHeader>
              <ModalIconWrap>
                <Wallet size={22} />
              </ModalIconWrap>
              <ModalTitle>Recharge wallet</ModalTitle>
              <ModalSubtitle>Add funds to your WhatsApp wallet</ModalSubtitle>
            </ModalHeader>

            {!showFeePreview ? (
              <>
                <InputGroup>
                  <InputLabel htmlFor="amount">Recharge amount (₹)</InputLabel>
                  <ModalInput
                    id="amount"
                    type="number"
                    placeholder="Enter amount (e.g. 500)"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(e.target.value)}
                    min="1"
                  />
                </InputGroup>
                <QuickAmountRow>
                  {[500, 1000, 2000, 5000].map((amt) => (
                    <QuickAmountButton
                      key={amt}
                      type="button"
                      $active={rechargeAmount === String(amt)}
                      onClick={() => setRechargeAmount(String(amt))}
                    >
                      ₹{amt.toLocaleString()}
                    </QuickAmountButton>
                  ))}
                </QuickAmountRow>
                <ModalActions>
                  <SecondaryButton onClick={handleCloseModal}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    disabled={
                      rechargeLoading ||
                      !rechargeAmount ||
                      parseFloat(rechargeAmount) <= 0
                    }
                    onClick={handleProceedRecharge}
                  >
                    {rechargeLoading ? (
                      <Loader2 size={16} className="spin" />
                    ) : (
                      "Proceed"
                    )}
                  </PrimaryButton>
                </ModalActions>
              </>
            ) : (
              rechargeOrder && (
                <>
                  <PreviewGrid>
                    <PreviewRow>
                      <span>Requested amount</span>
                      <span>
                        {formatCurrencyShort(rechargeOrder.amount_requested)}
                      </span>
                    </PreviewRow>
                    <PreviewRow>
                      <span>Gateway fee</span>
                      <span>
                        {formatCurrencyShort(rechargeOrder.gateway_fee)}
                      </span>
                    </PreviewRow>
                    <PreviewRow>
                      <span>GST (18% on fee)</span>
                      <span>
                        {formatCurrencyShort(rechargeOrder.gst_on_fee)}
                      </span>
                    </PreviewRow>
                    <TotalRow>
                      <span>Total amount</span>
                      <span>
                        {formatCurrencyShort(rechargeOrder.total_charged)}
                      </span>
                    </TotalRow>
                  </PreviewGrid>
                  <ModalActions>
                    <SecondaryButton onClick={handleCancelRecharge}>
                      Cancel order
                    </SecondaryButton>
                    <PrimaryButton onClick={handlePay}>
                      Pay {formatCurrencyShort(rechargeOrder.total_charged)}
                    </PrimaryButton>
                  </ModalActions>
                </>
              )
            )}
          </StyledModalContent>
        </ModalOverlay>
      )}

      {!walletData && !isError && (
        <EmptyState
          icon={<FileText size={48} />}
          title="No usage data yet"
          description="Usage and plan details will appear here once your WhatsApp account is connected."
        />
      )}
    </PageContainer>
  );
};

export default BillingUsagePage;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.18) 0%,
    rgba(206, 86, 176, 0.14) 55%,
    rgba(37, 211, 102, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  animation: ${fadeUp} 0.35s ease-out;
  @media ${device.tablet} {
    padding: 18px;
  }
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -45%;
  right: -6%;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 201, 81, 0.35) 0%,
    transparent 70%
  );
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  @media ${device.tablet} {
    flex-direction: column;
  }
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
  @media ${device.tablet} {
    font-size: 24px;
  }
`;

const PageSubtitle = styled.p`
  margin: 6px 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  &:hover {
    background: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
`;

const RefreshButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.9);
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.3);
    box-shadow: 0 4px 12px rgba(201, 77, 170, 0.1);
  }
  &:active svg {
    transform: rotate(180deg);
    transition: transform 0.4s ease;
  }
`;

const RechargeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.95) 0%,
    rgba(206, 86, 176, 0.95) 100%
  );
  color: white;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.24);
  transition: all 0.18s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.32);
  }
`;

const VerificationAlert = styled.div`
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 14px 18px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.1);

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 16px;
  animation: ${fadeUp} 0.4s ease-out 0.05s both;
  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 24px;
  color: #fff;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%);
  box-shadow: 0 12px 32px rgba(201, 77, 170, 0.28);
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 168px;
  &::after {
    content: "";
    position: absolute;
    right: -28px;
    bottom: -36px;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletHeroTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;
`;

const WalletIconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.92;
`;

const WalletHeroValue = styled.div`
  position: relative;
  z-index: 1;
  font-family: "Urbanist", sans-serif;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  @media ${device.tablet} {
    font-size: 28px;
  }
`;

const WalletHeroFooter = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const StatusPill = styled.span<{ $active: boolean }>`
  padding: 6px 12px;
  border-radius: 999px;
  font-family: "Urbanist", sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) =>
    p.$active ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.15)"};
  border: 1px solid rgba(255, 255, 255, 0.28);
`;

const QuickRechargeLink = styled.button`
  border: none;
  background: transparent;
  color: #fff;
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  opacity: 0.92;
  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 22px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-height: 168px;
`;

const StatIconWrap = styled.div<{ $tone: "credit" | "debit" }>`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => (p.$tone === "credit" ? "#16a34a" : "#dc2626")};
  background: ${(p) =>
    p.$tone === "credit" ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.1)"};
`;

const StatContent = styled.div`
  min-width: 0;
`;

const StatLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.02em;
`;

const UsageHighlight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(37, 211, 102, 0.08) 0%,
    rgba(37, 211, 102, 0.02) 100%
  );
  border: 1px solid rgba(37, 211, 102, 0.18);
  animation: ${fadeUp} 0.45s ease-out 0.1s both;
  @media ${device.tablet} {
    flex-wrap: wrap;
  }
`;

const UsageHighlightIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
  background: rgba(37, 211, 102, 0.14);
  flex-shrink: 0;
`;

const UsageHighlightContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const UsageHighlightLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  margin-bottom: 4px;
`;

const UsageHighlightValue = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.02em;
`;

const UsageHighlightNote = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.48);
  @media ${device.tablet} {
    width: 100%;
  }
`;

const BillingNoteCard = styled.div`
  padding: 16px 18px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(201, 77, 170, 0.14);
  border-left: 4px solid #c94daa;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
`;

const BillingNoteTitle = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 800;
  color: #c94daa;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const BillingNoteText = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(23, 23, 23, 0.68);
`;

const TransactionSection = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  animation: ${fadeUp} 0.5s ease-out 0.12s both;
`;

const SectionHeader = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px;
  font-family: "Urbanist", sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionHint = styled.p`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.52);
`;

const EmptyInvoices = styled.div`
  padding: 16px 0 8px;
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TransactionRow = styled.div<{ $type: string }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${(p) =>
    p.$type === "credit"
      ? "linear-gradient(135deg, rgba(34,197,94,0.06) 0%, #fff 100%)"
      : "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, #fff 100%)"};
  border: 1px solid
    ${(p) =>
    p.$type === "credit"
      ? "rgba(34, 197, 94, 0.12)"
      : "rgba(239, 68, 68, 0.1)"};
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  }
  @media ${device.tablet} {
    grid-template-columns: auto 1fr;
    gap: 12px;
  }
`;

const TransactionIcon = styled.div<{ $type: string }>`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => (p.$type === "credit" ? "#16a34a" : "#dc2626")};
  background: ${(p) =>
    p.$type === "credit" ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.1)"};
`;

const TransactionInfo = styled.div`
  min-width: 0;
`;

const TransactionDesc = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TransactionDate = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 3px;
`;

const TransactionRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  @media ${device.tablet} {
    grid-column: 2;
    align-items: flex-start;
  }
`;

const TransactionAmount = styled.div<{ $type: string }>`
  font-family: "Urbanist", sans-serif;
  font-size: 15px;
  font-weight: 800;
  white-space: nowrap;
  color: ${(p) => (p.$type === "credit" ? "#16a34a" : "#dc2626")};
`;

const TransactionBadge = styled.span<{ $type: string }>`
  font-size: 10px;
  font-weight: 700;
  padding: 4px 9px;
  border-radius: 999px;
  text-transform: capitalize;
  white-space: nowrap;
  background: ${(p) => (p.$type === "credit" ? "#dcfce7" : "#fee2e2")};
  color: ${(p) => (p.$type === "credit" ? "#166534" : "#991b1b")};
`;

const TabHeader = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  padding: 6px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 14px;
  border-radius: 10px;
  border: none;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#fff" : "transparent")};
  color: ${(p) => (p.$active ? "#c94daa" : "#6b7280")};
  box-shadow: ${(p) =>
    p.$active ? "0 4px 14px rgba(201, 77, 170, 0.12)" : "none"};
  transition: all 0.18s ease;
  &:hover {
    color: #c94daa;
  }
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #f8fafc;
  color: #6b7280;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  transition: all 0.15s;
  .spin {
    animation: ${spin} 1s linear infinite;
  }
  &:hover:not(:disabled) {
    background: #f1f5f9;
    color: #c94daa;
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  font-size: 10px;
  font-weight: 700;
  padding: 4px 9px;
  border-radius: 999px;
  text-transform: capitalize;
  white-space: nowrap;
  background: ${(p) =>
    p.$status === "paid" || p.$status === "credited" ? "#dcfce7" : "#fee2e2"};
  color: ${(p) =>
    p.$status === "paid" || p.$status === "credited" ? "#166534" : "#991b1b"};
`;

const StyledModalContent = styled(ModalContent)`
  max-width: 480px;
`;

const ModalHeader = styled.div`
  margin-bottom: 8px;
`;

const ModalIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
`;

const ModalSubtitle = styled.p`
  margin: 6px 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 16px;
`;

const InputLabel = styled.label`
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const ModalInput = styled.input`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  &:focus {
    border-color: #c94daa;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
`;

const QuickAmountRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 14px;
  @media ${device.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const QuickAmountButton = styled.button<{ $active: boolean }>`
  padding: 10px 8px;
  border-radius: 10px;
  border: 1px solid
    ${(p) => (p.$active ? "rgba(201, 77, 170, 0.35)" : "rgba(0, 0, 0, 0.08)")};
  background: ${(p) =>
    p.$active ? "rgba(201, 77, 170, 0.08)" : "#f8fafc"};
  color: ${(p) => (p.$active ? "#c94daa" : "#4b5563")};
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const PreviewGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.08) 0%,
    rgba(206, 86, 176, 0.06) 100%
  );
  padding: 18px;
  border-radius: 14px;
  margin-top: 16px;
  border: 1px solid rgba(201, 77, 170, 0.1);
`;

const PreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  color: #171717;
  font-weight: 800;
  border-top: 1px dashed rgba(201, 77, 170, 0.2);
  padding-top: 12px;
`;
